import { Injectable } from "@angular/core";
import { Font } from "../../../domain/font";
import { DynamicField, DynamicType } from "../../../domain/dynamic-field";
import { Country } from "../../../domain/country";
import { RoleNames, User, UserChannelPermission } from "../../../domain/user";
import { DynamicScript } from "../../../domain/dynamic-script";
import { S3Bucket } from "../../../domain/tenant-storage-option";
import { EStorageProvider } from "../../../domain/Upload/e-storage-provider";
import { Tenant } from "../../../domain/tenant";
import { EdgeServer } from "../../../domain/edge-server";

function toCountries(apiResult) {
  return apiResult.countriesList.map(
    (country) => new Country(country.CountryId, country.CountryName),
  );
}

function toDynamicFields(apiResult): DynamicField[] {
  return apiResult.map((dynamicField) => {
    let linkType;
    switch (dynamicField.link_type) {
      case "Text":
        linkType = DynamicType.Text;
        break;
      case "Image":
        linkType = DynamicType.Image;
        break;
      case "Video":
        linkType = DynamicType.Video;
    }
    return new DynamicField(
      dynamicField.id,
      dynamicField.name,
      dynamicField.description,
      linkType,
    );
  });
}

function toFonts(apiResult): Font[] {
  return apiResult.map((font) => new Font(font.FontId, font.FontName));
}

function toDynamicScripts(apiResult): DynamicScript[] {
  return apiResult.map(
    (dynamicScript) =>
      new DynamicScript(
        dynamicScript.id,
        dynamicScript.html,
        dynamicScript.ScriptName,
        dynamicScript.ShowScriptOnTopRight,
        dynamicScript.ShowScriptOnRightClickWithContent,
        dynamicScript.ShowScriptOnRightClickWithoutContent,
      ),
  );
}

function toTimezones(apiResult): [{ id: number; name: string; value: string }] {
  return apiResult.map((timezone) => {
    return {
      id: timezone.Id,
      name: timezone.Name,
      value: timezone.Value,
    };
  });
}

function toLanguages(apiResult): [{ code: string; name: string }] {
  return apiResult.map((language) => {
    return { code: language.Code, name: language.Name };
  });
}

export function toMetadata(apiResult) {
  return apiResult.map((metadata) => {
    return { id: metadata.Id, name: metadata.Name };
  });
}

function toTenantStorageOptions(apiResult) {
  return apiResult.map((x) => {
    return new S3Bucket(
      x.Id,
      x.DisplayName,
      x.BucketName,
      x.RegionEndpoint,
      x.AccessKey,
    );
  });
}

@Injectable()
export class UserMapper {
  toUser(
    initResponse,
    fontsResponse,
    dynamicScriptsResponse,
    timezonesResponse,
    languagesResponse,
    metadataResponse,
    tenantStorageOptionsResponse,
    channelAvailabilityZonesResponse,
  ) {
    const fonts = toFonts(fontsResponse);
    const countries = toCountries(initResponse);
    const dynamicScripts = toDynamicScripts(dynamicScriptsResponse);
    const phone = initResponse.phone || ""; // need to inplements
    const userId = initResponse.userId;
    const username = initResponse.userName;
    const tenantGuid = initResponse.tenantGuid;
    const tenantId = initResponse.tenantId;
    const email = initResponse.email;
    const firstName = initResponse.firstName;
    const lastName = initResponse.lastName;
    const liveBroadcastingCheckPeriodMs =
      initResponse.liveBroadcastingCheckPeriodMs;
    const timezones = toTimezones(timezonesResponse);
    const languages = toLanguages(languagesResponse);

    const trialEndsDate = initResponse.trialEndsDate
      ? new Date(initResponse.trialEndsDate)
      : null;
    const lastAuth = initResponse.lastAuth
      ? new Date(initResponse.lastAuth)
      : null;

    const metadata = toMetadata(metadataResponse);
    const tenantStorageOptions = toTenantStorageOptions(
      tenantStorageOptionsResponse,
    );

    const channelAvailabilityZones = channelAvailabilityZonesResponse.map(
      (x) => {
        return {
          id: x.Id,
          name: x.Name,
          displayName: x.DisplayName,
          previewUrl: x.PreviewUrl,
          htmlColor: x.HtmlColor,
          isActive: x.IsActive,
        };
      },
    );

    const hasTwoFactorAuth = initResponse.hasTwoFactor;
    // console.log('init', initResponse);
    const storageProvider =
      initResponse.cloudStorageProvider as EStorageProvider;
    /*const roles = {};
    roles[RoleNames.BatchUpload] = true;
    roles[RoleNames.MAM_Editor] = true;
    roles[RoleNames.DownloadMedia] = true;
    roles[RoleNames.ChannelCreator] = true;
    roles[RoleNames.RecordingManager] = true;
    roles[RoleNames.UsersManager] = true;
    // roles[RoleNames.SuperUser] = true;
    const channelPermissions = {};
    channelPermissions[20310] = new UserChannelPermission(true, true, false, true);
    channelPermissions[20057] = new UserChannelPermission(true, true, true, true);*/

    const roles = {};
    const channelPermissions = {};
    if (initResponse.roles && initResponse.roles.length > 0) {
      initResponse.roles.map((roleName) => (roles[roleName] = true));
    }
    if (
      initResponse.channelPermissions &&
      initResponse.channelPermissions.length > 0
    ) {
      initResponse.channelPermissions
        // tslint:disable-next-line:max-line-length
        .map(
          (channelPermission) =>
            (channelPermissions[channelPermission.ChannelId] =
              new UserChannelPermission(
                channelPermission.ChannelId,
                channelPermission.View,
                channelPermission.EditSchedule,
                channelPermission.EditSettings,
                channelPermission.ActivateChannel,
              )),
        );
    }
    //const managedTenants = initResponse.managedTenants.map(t => new Tenant(t.TenantId, t.TenantGuid, t.TenantName));
    let managedTenants = [];
    if (initResponse.managedTenants && initResponse.managedTenants.length > 0) {
      managedTenants = initResponse.managedTenants.map(
        (t) => new Tenant(t.TenantId, t.TenantGuid, t.TenantName),
      );
    }
    const edgeServers = initResponse.edgeServers.map(
      (r) => new EdgeServer(r.Id, r.Name, r.Ip, r.AvailabilityZoneId),
    );

    let broadcastPresetAssetLimitsPerType = [];
    if (initResponse.broadcastPresetAssetLimitsPerType) {
      broadcastPresetAssetLimitsPerType = initResponse.broadcastPresetAssetLimitsPerType.split(',').map(num => {
        return parseInt(num);
      });
    }
    let broadcastPresetAssetIdPerTypeLimits = [];
    if (initResponse.broadcastPresetAssetIdPerTypeLimits) {
      broadcastPresetAssetIdPerTypeLimits = initResponse.broadcastPresetAssetIdPerTypeLimits.split(',').map(num => {
        return parseInt(num);
      });
    }
    return new User(
      userId,
      username,
      email,
      firstName,
      lastName,
      countries,
      managedTenants.map((t) => t.id),
      tenantGuid,
      tenantId,
      phone,
      fonts,
      broadcastPresetAssetIdPerTypeLimits,
      broadcastPresetAssetLimitsPerType,
      dynamicScripts,
      timezones,
      languages,
      roles,
      channelPermissions,
      metadata,
      tenantStorageOptions,
      channelAvailabilityZones,
      edgeServers,
      storageProvider,
      managedTenants,
      liveBroadcastingCheckPeriodMs,
      trialEndsDate,
      hasTwoFactorAuth,
      lastAuth,
      initResponse.previewUrlPrefix,
    );
  }
}
